import React, { useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';

import { useNotification } from '../../hooks/NotificationContext';
import { inviteuser, inviteUsersBulk } from '../../modules/users';
import AddUserForm from './AddUserForm';
import BulkInviteUser from './BulkInviteUser';
import { useInvalidateQuery } from '../../query/apiQueries';
import JiraUserMappingTableModal from '../integrations/JiraUserMappingPage';
import { useQueryClient } from '@tanstack/react-query';
import Modal from '../../ui/layout/Modal';

export default function AddUser({ fetchUsersData, open, setOpen }) {
  const { addNotification } = useNotification();

  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(0);
  const [inviteResults, setInviteResults] = useState(null);

  const [mappingProjects, setMappingProjects] = useState([]);
  const [currentProject, setCurrentProject] = useState(null);

  const addUserFormRef = useRef(null);
  const bulkInviteRef = useRef(null);

  const queryClient = useQueryClient();
  const invalidateQueries = useInvalidateQuery();

  const handleSingleInvite = async (values) => {
    const { projects = [], ...rest } = values;

    const userPayload = {
      ...rest,
      projects,
      terms_conditions: true,
      platform: 'web',
      password: process.env.REACT_APP_PASSWORD_SECRET_KEY || 'invited_user',
    };

    setLoading(true);

    try {
      await inviteuser(userPayload);

      addNotification(
        'success',
        'An invite has been sent to the user! They will receive an email shortly.',
      );

      fetchUsersData();

      invalidateQueries([{ key: 'user', type: 'list' }]);

      setOpen(false);

      const jiraProjects = projects.filter((p) => p.jiraCloudId);

      if (jiraProjects.length > 0) {
        const cloudGroups = {};

        jiraProjects.forEach((p) => {
          if (!cloudGroups[p.jiraCloudId]) {
            cloudGroups[p.jiraCloudId] = [];
          }

          cloudGroups[p.jiraCloudId].push(p);
        });

        const groupedProjects = Object.values(cloudGroups).map(
          (group) => group[0],
        );

        setMappingProjects(groupedProjects);
        setCurrentProject(groupedProjects[0]);

        groupedProjects.forEach((p) =>
          queryClient.invalidateQueries({
            queryKey: ['jira-mappings', p.id],
          }),
        );
      }
    } catch (error) {
      addNotification(
        'error',
        error.message || 'Unable to invite user.',
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBulkInvite = async (users) => {
    setLoading(true);
    setInviteResults(null);

    try {
      const response = await inviteUsersBulk(users);

      setInviteResults(response.results);

      if (response.results.invited.length > 0) {
        addNotification(
          'success',
          'Invites have been sent to the users! They will receive an email shortly.',
        );
      }

      fetchUsersData();

      invalidateQueries([{ key: 'user', type: 'list' }]);
    } catch (error) {
      addNotification(
        'error',
        error.message || 'Bulk invite failed.',
      );
    } finally {
      setLoading(false);
    }
  };

  const handleModalClose = () => {
    setOpen(false);
    setTab(0);
    setInviteResults(null);
    setLoading(false);
  };

  const handleMappingClose = () => {
    const remaining = mappingProjects.slice(1);

    if (remaining.length > 0) {
      setMappingProjects(remaining);
      setCurrentProject(remaining[0]);
    } else {
      setMappingProjects([]);
      setCurrentProject(null);
    }
  };

  return (
    <>
      <Modal
        open={open}
        handleClose={handleModalClose}
        modalTitle="Invite Members"
        width="sm"
        action={() => {
          if (tab === 1 && inviteResults) {
            return (
              <>
                <Button
                  variant="outlined"
                  onClick={handleModalClose}
                >
                  Close
                </Button>

                <Button
                  variant="contained"
                  onClick={() =>
                    bulkInviteRef.current?.reset()
                  }
                >
                  {inviteResults.failed.length > 0
                    ? 'Try Again'
                    : 'Invite More Users'}
                </Button>
              </>
            );
          }

          return (
            <>
              <Button
                variant="outlined"
                onClick={handleModalClose}
                disabled={loading}
              >
                Cancel
              </Button>

              {tab === 0 ? (
                <Button
                  variant="contained"
                  disabled={loading}
                  onClick={() =>
                    addUserFormRef.current?.submit()
                  }
                >
                  {loading
                    ? 'Inviting...'
                    : 'Send Invite'}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  disabled={
                    loading ||
                    bulkInviteRef.current?.isDisabled
                  }
                  onClick={() =>
                    bulkInviteRef.current?.submit()
                  }
                >
                  {loading
                    ? 'Inviting...'
                    : 'Invite Users'}
                </Button>
              )}
            </>
          );
        }}
      >
        <Tabs
          value={tab}
          onChange={(_, value) => setTab(value)}
          sx={{ mb: 3 }}
        >
          <Tab label="Add User" />
          <Tab label="Bulk Invite" />
        </Tabs>

        {loading ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            py={6}
            gap={2}
          >
            <CircularProgress size={32} />
            <Typography variant="body2" color="text.secondary">
              Inviting users...
            </Typography>
          </Box>
        ) : tab === 0 ? (
          <AddUserForm
            handleSubmit={handleSingleInvite}
            loading={loading}
            ref={addUserFormRef}
          />
        ) : (
          <BulkInviteUser
            handleSubmit={handleBulkInvite}
            loading={loading}
            inviteResults={inviteResults}
            resetResults={() =>
              setInviteResults(null)
            }
            ref={bulkInviteRef}
          />
        )}
      </Modal>

      {currentProject && (
        <JiraUserMappingTableModal
          project={currentProject}
          open={!!currentProject}
          handleClose={handleMappingClose}
          isAdmin
          isAddUserFlow
        />
      )}
    </>
  );
}